import { TableCell, TableRow } from "@mui/material";
import { useEffect, useRef } from "react";

import InlineNameField, { type InlineNameEdit } from "./InlineNameField";
import { FROZEN_COLUMN_COUNT, FROZEN_RIGHT, ROW_BG, frozenCellSx } from "./frozenColumns";

interface NewProjectRowProps extends InlineNameEdit {
  /** Pre-filled and selected, so typing replaces it. A blank name keeps it. */
  initialName: string;
  /** Total columns in the table; the name spans all but the frozen ones. */
  colSpan: number;
}

/**
 * A project that doesn't exist yet: the first project row, its name selected
 * and editable. Enter or clicking away moves on to setting the project up;
 * Escape drops it.
 */
export default function NewProjectRow({ initialName, colSpan, ...edit }: NewProjectRowProps) {
  const rowRef = useRef<HTMLTableRowElement | null>(null);
  // Below a long folder list the row starts out of view. Centred, so the
  // table's sticky header can't cover it.
  useEffect(() => {
    rowRef.current?.scrollIntoView({ block: "center" });
  }, []);

  return (
    <TableRow
      ref={rowRef}
      sx={{
        borderLeft: "3px solid #00d4ad",
        backgroundColor: ROW_BG.highlightProject,
      }}
    >
      <TableCell colSpan={Math.max(1, colSpan - FROZEN_COLUMN_COUNT)} sx={{ minWidth: 200 }}>
        <InlineNameField
          initialName={initialName}
          ariaLabel="Project name"
          accent="#00d4ad"
          hint="Press Enter to set up the project or Esc to cancel"
          messages="caption"
          {...edit}
        />
      </TableCell>
      {/* Empty frozen cells keep the row's colour under the sticky columns. */}
      <TableCell padding="none" sx={frozenCellSx(FROZEN_RIGHT.pin)} />
      <TableCell padding="none" sx={frozenCellSx(FROZEN_RIGHT.eye)} />
      <TableCell padding="none" sx={frozenCellSx(FROZEN_RIGHT.locate)} />
      <TableCell padding="none" sx={frozenCellSx(FROZEN_RIGHT.menu, true)} />
    </TableRow>
  );
}
